/**
 * This composable encapsulates the OAuth 2.0 Authorization Code flow with PKCE
 *  for initiating a login redirect to an authorization server.
 *  It generates a PKCE code verifier and challenge, persists the verifier and a
 *  post-login redirect target in sessionStorage, then navigates the browser to
 *  the provider's /oauth/authorize endpoint.
 */
import { generateCodeChallenge, generateCodeVerifier } from "../composables/usePkce"
import { ref } from "vue"
import { url } from "../helper"
import { Browser } from "@capacitor/browser"
import { useDeviceManager } from "../stores/deviceManager"
import { useI18n } from "vue-i18n"

import { Capacitor } from "@capacitor/core"
import { Device } from "@capacitor/device"
const codeVerifier = ref("")

// OAuth client identifier registered with the authorization server.
const clientId = "zQTiAtJD02-HEn6fvUTE_I2Y5iKFkA9qJN4vkVDKNbI"

/**  The redirect URI the auth server will send the user back to after login.
 *  Typically this maps to a route in the SPA that handles the callback and
 *  completes the token exchange using the stored PKCE code verifier.
 */

export function useLogin() {
	// const redirectUri = "valormovies://callback"

	const login = async (redirectToAfterLogin: string = "/profile") => {
		const languageCode = await Device.getLanguageCode()
		// console.log("Login called with redirectToAfterLogin:", redirectToAfterLogin)
		const deviceManager = useDeviceManager()
		const redirectUri =
			// deviceManager.operatingSystem === "ios" || deviceManager.operatingSystem === "android"
			Capacitor.isNativePlatform() ? "valormovies://callback" : window.location.origin + "/callback"
		// console.log("redirect uri", redirectUri)
		// Generate a one-time PKCE code verifier and its derived code challenge.
		// console.log("Generating code verifier and challenge...")

		// try {
		const codeVerifier = generateCodeVerifier()
		const codeChallenge = await generateCodeChallenge(codeVerifier)
		// } catch (error) {
		// 	console.error("Error generating PKCE code challenge:", error)
		// 	alert("Error generating PKCE code challenge: " + error)
		// }

		// Persist values needed during the callback handler to complete the
		// authorization code exchange and to restore post-login navigation.
		sessionStorage.setItem("pkce_code_verifier", codeVerifier)
		sessionStorage.setItem("post_login_redirect", redirectToAfterLogin)

		/**  Construct the authorization endpoint URL with required query params:
		 *  response_type=code: requests an authorization code
		 *  client_id: this app's registered client identifier
		 *  redirect_uri: where the auth server should send the user back
		 *  code_challenge and method S256: PKCE parameters derived from verifier
		 *  scope: requested permissions (here, "access_token" scope placeholder)
		 */
		// console.log("Constructing authorization URL...", languageCode.value)
		let authUrl =
			`${url}/${languageCode.value}/oauth/authorize?` +
			`response_type=code` +
			`&client_id=${clientId}` +
			`&redirect_uri=${encodeURIComponent(redirectUri)}` +
			`&code_challenge=${codeChallenge}` +
			`&code_challenge_method=S256` +
			`&scope=access_token`
		if (Capacitor.isNativePlatform()) {
			authUrl += "&mobile_app=1"
		}
		// console.log("Auth URL:", authUrl)
		// Use Capacitor Browser for native apps, regular redirect for web
		if (
			deviceManager.operatingSystem === "ios" ||
			deviceManager.operatingSystem === "android" ||
			Capacitor.isNativePlatform()
		) {
			await Browser.open({ url: authUrl })
		} else {
			window.location.href = authUrl
		}
	}

	// Expose the login action to components that use this composable.
	return {
		login,
	}
}
